import { createSlice } from "@reduxjs/toolkit";
import {
  getSpecificationDetailThunk,
  getSpecificationItemStatsThunk,
  getSpecificationPinnedProductsThunk,
  getSpecificationScoredProductsThunk,
  getSpecificationCandidateProductsThunk,
  getSpecificationContributorsThunk,
  getSupplierResponseDetailsThunk,
  getSupplierStatisticsThunk,
  getConflictingItemsThunk,
  getSpecificationProductStatsThunk,
  getNotFoundItemsThunk,
  getSpecificationScreenTimeStatsThunk,
  getSpecificationCreditStatsThunk,
} from "../thunks/reportThunk";
import {
  ISpecificationDetailResponse,
  ISpecificationItemStatsResponse,
  ISpecificationPinnedProductsResponse,
  ISpecificationScoredProductsResponse,
  ISpecificationContributorsResponse,
  ISupplierResponseDetailsResponse,
  ISupplierStatisticsResponse,
  IConflictingItemsResponse,
  ISpecificationProductStatsResponse,
  INotFoundItemsResponse,
  ISpecificationScreenTimeStatsResponse,
  ISpecificationCreditStatsResponse,
} from "@/shared/services/interface/responses/IReportResponse";
import { ISpecificationCandidateProductsResponse } from "@/shared/services/interface/responses/ISpecificationCandidateProductsResponse";

interface IReportState {
  specificationDetail: ISpecificationDetailResponse | null;
  specificationItemStats: ISpecificationItemStatsResponse | null;
  specificationPinnedProducts: ISpecificationPinnedProductsResponse | null;
  specificationScoredProducts: ISpecificationScoredProductsResponse | null;
  specificationCandidateProducts: ISpecificationCandidateProductsResponse | null;
  specificationContributors: ISpecificationContributorsResponse | null;
  supplierResponseDetails: ISupplierResponseDetailsResponse | null;
  supplierStatistics: ISupplierStatisticsResponse | null;
  specificationScreenTimeStats: ISpecificationScreenTimeStatsResponse | null;
  conflictingItems: IConflictingItemsResponse | null;
  specificationProductStats: ISpecificationProductStatsResponse | null;
  notFoundItems: INotFoundItemsResponse | null;
  specificationCreditStats: ISpecificationCreditStatsResponse | null;
  loading: boolean;
  itemStatsLoading: boolean;
  pinnedProductsLoading: boolean;
  scoredProductsLoading: boolean;
  candidateProductsLoading: boolean;
  contributorsLoading: boolean;
  supplierResponseLoading: boolean;
  supplierStatisticsLoading: boolean;
  screenTimeStatsLoading: boolean;
  conflictingItemsLoading: boolean;
  productStatsLoading: boolean;
  notFoundItemsLoading: boolean;
  creditStatsLoading: boolean;
  error: string | null;
  itemStatsError: string | null;
  pinnedProductsError: string | null;
  scoredProductsError: string | null;
  candidateProductsError: string | null;
  contributorsError: string | null;
  supplierResponseError: string | null;
  supplierStatisticsError: string | null;
  screenTimeStatsError: string | null;
  conflictingItemsError: string | null;
  productStatsError: string | null;
  notFoundItemsError: string | null;
  creditStatsError: string | null;
}

const initialState: IReportState = {
  specificationDetail: null,
  specificationItemStats: null,
  specificationPinnedProducts: null,
  specificationScoredProducts: null,
  specificationCandidateProducts: null,
  specificationContributors: null,
  supplierResponseDetails: null,
  supplierStatistics: null,
  specificationScreenTimeStats: null,
  conflictingItems: null,
  specificationProductStats: null,
  notFoundItems: null,
  specificationCreditStats: null,
  loading: false,
  itemStatsLoading: false,
  pinnedProductsLoading: false,
  scoredProductsLoading: false,
  candidateProductsLoading: false,
  contributorsLoading: false,
  supplierResponseLoading: false,
  supplierStatisticsLoading: false,
  screenTimeStatsLoading: false,
  conflictingItemsLoading: false,
  productStatsLoading: false,
  notFoundItemsLoading: false,
  creditStatsLoading: false,
  error: null,
  itemStatsError: null,
  pinnedProductsError: null,
  scoredProductsError: null,
  candidateProductsError: null,
  contributorsError: null,
  supplierResponseError: null,
  supplierStatisticsError: null,
  screenTimeStatsError: null,
  conflictingItemsError: null,
  productStatsError: null,
  notFoundItemsError: null,
  creditStatsError: null,
};

export const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearItemStatsError: (state) => {
      state.itemStatsError = null;
    },
    clearPinnedProductsError: (state) => {
      state.pinnedProductsError = null;
    },
    clearScoredProductsError: (state) => {
      state.scoredProductsError = null;
    },
    clearCandidateProductsError: (state) => {
      state.candidateProductsError = null;
    },
    clearContributorsError: (state) => {
      state.contributorsError = null;
    },
    clearSupplierResponseError: (state) => {
      state.supplierResponseError = null;
    },
    clearSupplierStatisticsError: (state) => {
      state.supplierStatisticsError = null;
    },
    clearScreenTimeStatsError: (state) => {
      state.screenTimeStatsError = null;
    },
    clearConflictingItemsError: (state) => {
      state.conflictingItemsError = null;
    },
    clearSpecificationDetail: (state) => {
      state.specificationDetail = null;
    },
    clearSpecificationItemStats: (state) => {
      state.specificationItemStats = null;
    },
    clearSpecificationPinnedProducts: (state) => {
      state.specificationPinnedProducts = null;
    },
    clearSpecificationScoredProducts: (state) => {
      state.specificationScoredProducts = null;
    },
    clearSpecificationCandidateProducts: (state) => {
      state.specificationCandidateProducts = null;
    },
    clearSpecificationContributors: (state) => {
      state.specificationContributors = null;
    },
    clearSupplierResponseDetails: (state) => {
      state.supplierResponseDetails = null;
    },
    clearSupplierStatistics: (state) => {
      state.supplierStatistics = null;
    },
    clearSpecificationScreenTimeStats: (state) => {
      state.specificationScreenTimeStats = null;
    },
    clearConflictingItems: (state) => {
      state.conflictingItems = null;
    },
    clearProductStatsError: (state) => {
      state.productStatsError = null;
    },
    clearNotFoundItemsError: (state) => {
      state.notFoundItemsError = null;
    },
    clearSpecificationProductStats: (state) => {
      state.specificationProductStats = null;
    },
    clearNotFoundItems: (state) => {
      state.notFoundItems = null;
    },
    clearCreditStatsError: (state) => {
      state.creditStatsError = null;
    },
    clearSpecificationCreditStats: (state) => {
      state.specificationCreditStats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSpecificationDetailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSpecificationDetailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.specificationDetail = action.payload;
        state.error = null;
      })
      .addCase(getSpecificationDetailThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSpecificationItemStatsThunk.pending, (state) => {
        state.itemStatsLoading = true;
        state.itemStatsError = null;
      })
      .addCase(getSpecificationItemStatsThunk.fulfilled, (state, action) => {
        state.itemStatsLoading = false;
        state.specificationItemStats = action.payload;
        state.itemStatsError = null;
      })
      .addCase(getSpecificationItemStatsThunk.rejected, (state, action) => {
        state.itemStatsLoading = false;
        state.itemStatsError = action.payload as string;
      })
      .addCase(getSpecificationPinnedProductsThunk.pending, (state) => {
        state.pinnedProductsLoading = true;
        state.pinnedProductsError = null;
      })
      .addCase(
        getSpecificationPinnedProductsThunk.fulfilled,
        (state, action) => {
          state.pinnedProductsLoading = false;
          state.specificationPinnedProducts = action.payload;
          state.pinnedProductsError = null;
        }
      )
      .addCase(
        getSpecificationPinnedProductsThunk.rejected,
        (state, action) => {
          state.pinnedProductsLoading = false;
          state.pinnedProductsError = action.payload as string;
        }
      )
      .addCase(getSpecificationScoredProductsThunk.pending, (state) => {
        state.scoredProductsLoading = true;
        state.scoredProductsError = null;
      })
      .addCase(
        getSpecificationScoredProductsThunk.fulfilled,
        (state, action) => {
          state.scoredProductsLoading = false;
          state.specificationScoredProducts = action.payload;
          state.scoredProductsError = null;
        }
      )
      .addCase(
        getSpecificationScoredProductsThunk.rejected,
        (state, action) => {
          state.scoredProductsLoading = false;
          state.scoredProductsError = action.payload as string;
        }
      )
      .addCase(getSpecificationCandidateProductsThunk.pending, (state) => {
        state.candidateProductsLoading = true;
        state.candidateProductsError = null;
      })
      .addCase(
        getSpecificationCandidateProductsThunk.fulfilled,
        (state, action) => {
          state.candidateProductsLoading = false;
          state.specificationCandidateProducts = action.payload;
          state.candidateProductsError = null;
        }
      )
      .addCase(
        getSpecificationCandidateProductsThunk.rejected,
        (state, action) => {
          state.candidateProductsLoading = false;
          state.candidateProductsError = action.payload as string;
        }
      )
      .addCase(getSpecificationContributorsThunk.pending, (state) => {
        state.contributorsLoading = true;
        state.contributorsError = null;
      })
      .addCase(getSpecificationContributorsThunk.fulfilled, (state, action) => {
        state.contributorsLoading = false;
        state.specificationContributors = action.payload;
        state.contributorsError = null;
      })
      .addCase(getSpecificationContributorsThunk.rejected, (state, action) => {
        state.contributorsLoading = false;
        state.contributorsError = action.payload as string;
      })
      .addCase(getSupplierResponseDetailsThunk.pending, (state) => {
        state.supplierResponseLoading = true;
        state.supplierResponseError = null;
      })
      .addCase(getSupplierResponseDetailsThunk.fulfilled, (state, action) => {
        state.supplierResponseLoading = false;
        state.supplierResponseDetails = action.payload;
        state.supplierResponseError = null;
      })
      .addCase(getSupplierResponseDetailsThunk.rejected, (state, action) => {
        state.supplierResponseLoading = false;
        state.supplierResponseError = action.payload as string;
      })
      .addCase(getSupplierStatisticsThunk.pending, (state) => {
        state.supplierStatisticsLoading = true;
        state.supplierStatisticsError = null;
      })
      .addCase(getSupplierStatisticsThunk.fulfilled, (state, action) => {
        state.supplierStatisticsLoading = false;
        state.supplierStatistics = action.payload;
        state.supplierStatisticsError = null;
      })
      .addCase(getSupplierStatisticsThunk.rejected, (state, action) => {
        state.supplierStatisticsLoading = false;
        state.supplierStatisticsError = action.payload as string;
      })
      .addCase(getSpecificationScreenTimeStatsThunk.pending, (state) => {
        state.screenTimeStatsLoading = true;
        state.screenTimeStatsError = null;
      })
      .addCase(
        getSpecificationScreenTimeStatsThunk.fulfilled,
        (state, action) => {
          state.screenTimeStatsLoading = false;
          state.specificationScreenTimeStats = action.payload;
          state.screenTimeStatsError = null;
        }
      )
      .addCase(
        getSpecificationScreenTimeStatsThunk.rejected,
        (state, action) => {
          state.screenTimeStatsLoading = false;
          state.screenTimeStatsError = action.payload as string;
        }
      )
      .addCase(getConflictingItemsThunk.pending, (state) => {
        state.conflictingItemsLoading = true;
        state.conflictingItemsError = null;
      })
      .addCase(getConflictingItemsThunk.fulfilled, (state, action) => {
        state.conflictingItemsLoading = false;
        state.conflictingItems = action.payload;
        state.conflictingItemsError = null;
      })
      .addCase(getConflictingItemsThunk.rejected, (state, action) => {
        state.conflictingItemsLoading = false;
        state.conflictingItemsError = action.payload as string;
      })
      .addCase(getSpecificationProductStatsThunk.pending, (state) => {
        state.productStatsLoading = true;
        state.productStatsError = null;
      })
      .addCase(getSpecificationProductStatsThunk.fulfilled, (state, action) => {
        state.productStatsLoading = false;
        state.specificationProductStats = action.payload;
        state.productStatsError = null;
      })
      .addCase(getSpecificationProductStatsThunk.rejected, (state, action) => {
        state.productStatsLoading = false;
        state.productStatsError = action.payload as string;
      })
      .addCase(getNotFoundItemsThunk.pending, (state) => {
        state.notFoundItemsLoading = true;
        state.notFoundItemsError = null;
      })
      .addCase(getNotFoundItemsThunk.fulfilled, (state, action) => {
        state.notFoundItemsLoading = false;
        state.notFoundItems = action.payload;
        state.notFoundItemsError = null;
      })
      .addCase(getNotFoundItemsThunk.rejected, (state, action) => {
        state.notFoundItemsLoading = false;
        state.notFoundItemsError = action.payload as string;
      })
      .addCase(getSpecificationCreditStatsThunk.pending, (state) => {
        state.creditStatsLoading = true;
        state.creditStatsError = null;
      })
      .addCase(
        getSpecificationCreditStatsThunk.fulfilled,
        (state, action) => {
          state.creditStatsLoading = false;
          state.specificationCreditStats = action.payload;
          state.creditStatsError = null;
        }
      )
      .addCase(
        getSpecificationCreditStatsThunk.rejected,
        (state, action) => {
          state.creditStatsLoading = false;
          state.creditStatsError = action.payload as string;
        }
      );
  },
});

export const {
  clearError,
  clearItemStatsError,
  clearPinnedProductsError,
  clearScoredProductsError,
  clearCandidateProductsError,
  clearContributorsError,
  clearSupplierResponseError,
  clearSupplierStatisticsError,
  clearScreenTimeStatsError,
  clearConflictingItemsError,
  clearProductStatsError,
  clearSpecificationDetail,
  clearSpecificationItemStats,
  clearSpecificationPinnedProducts,
  clearSpecificationScoredProducts,
  clearSpecificationCandidateProducts,
  clearSpecificationContributors,
  clearSupplierResponseDetails,
  clearSupplierStatistics,
  clearSpecificationScreenTimeStats,
  clearConflictingItems,
  clearNotFoundItemsError,
  clearNotFoundItems,
  clearSpecificationProductStats,
  clearCreditStatsError,
  clearSpecificationCreditStats,
} = reportSlice.actions;
export default reportSlice.reducer;
